import React, { createRef, ReactNode, RefObject, useContext, useRef } from 'react';
import { NavLink } from 'react-router';
import './List.scss';
import { IListElement } from '@/types';
import { MenuContext } from '../MenuContext';
import clsx from 'clsx';
import Tooltip from '../../Tooltip';
import { ITooltipProps } from '../../Tooltip/Tooltip';

interface IListProps {
    /** Элементы меню */
    list: IListElement[];
    /** Цвет tooltip */
    tooltipBackground?: ITooltipProps['background'];
    className?: string;
    handlerType?: 'click' | 'mouseDown' | 'mouseUp';
    /** Глобальный рендерер endAdornment (fallback, если у элемента не задан) */
    renderEndAdornment?: (li: IListElement, index: number) => ReactNode;
}

const EndAdornmentContainer: React.FC<{ children?: ReactNode }> = ({ children }) => {
    if (!children) return null;
    return (
        <div
            className="rf-list__end-adornment"
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            role="presentation"
        >
            {children}
        </div>
    );
};

const List: React.FC<IListProps> = ({
    list,
    tooltipBackground = 'white',
    className,
    handlerType = 'click',
    renderEndAdornment,
}) => {
    const refs = useRef<RefObject<HTMLDivElement | HTMLAnchorElement | null>[]>([]);
    const { onClose } = useContext(MenuContext);

    const onElementClick = (e: React.MouseEvent, li: IListElement) => {
        e.stopPropagation();
        !li.url && e.preventDefault();
        li.handler?.();
        onClose?.();
    };

    const checkOverflow = (index: number) => {
        const el = refs.current[index]?.current;
        if (!el) return false;
        return el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
    };

    const renderLabel = (index: number, label: ReactNode) =>
        checkOverflow(index) ? (
            <Tooltip background={tooltipBackground} position="bottom">
                <span>{label}</span>
                <>{label}</>
            </Tooltip>
        ) : (
            <>{label}</>
        );

    const renderEnd = (li: IListElement, index: number) => {
        const local = typeof li.endAdornment === 'function' ? li.endAdornment({ item: li, index }) : li.endAdornment;

        return local ?? renderEndAdornment?.(li, index) ?? null;
    };

    const listElementJSX = list?.map((li, i) => {
        const disabledClass = li.disabled ? 'rf-list__element--disabled' : '';
        const separatedClass = li.separated ? 'rf-list__element--separated' : '';
        if (!refs.current[i]) refs.current[i] = createRef();

        const content = (
            <>
                <div className="rf-list__element-label">{renderLabel(i, li.label)}</div>
                <EndAdornmentContainer>{renderEnd(li, i)}</EndAdornmentContainer>
            </>
        );

        return (
            <li className="rf-li" key={`${li.value ?? 'item'}_${i}`}>
                {li.separated && <div className={clsx('rf-list__separator', 'rf-list__separator--top')} />}
                {li.url ? (
                    <NavLink
                        to={li.url}
                        style={li.style}
                        className={clsx('rf-list__element', disabledClass, separatedClass)}
                        data-testid={`rf-list-element-${li.value || i}`}
                        onClick={(e) => handlerType === 'click' && onElementClick(e, li)}
                        onMouseDown={(e) => handlerType === 'mouseDown' && onElementClick(e, li)}
                        onMouseUp={(e) => handlerType === 'mouseUp' && onElementClick(e, li)}
                        title={li.tooltip}
                    >
                        {content}
                    </NavLink>
                ) : (
                    <div
                        style={li.style}
                        ref={refs.current[i] as RefObject<HTMLDivElement>}
                        className={clsx(
                            'rf-list__element',
                            disabledClass,
                            separatedClass,
                            li.isTitle && 'rf-list__element--title',
                        )}
                        data-testid={`rf-list-element-${li.value || i}`}
                        onClick={(e) =>
                            !(li.isTitle || li.preventClick) && handlerType === 'click' && onElementClick(e, li)
                        }
                        onMouseDown={(e) =>
                            !(li.isTitle || li.preventClick) && handlerType === 'mouseDown' && onElementClick(e, li)
                        }
                        onMouseUp={(e) =>
                            !(li.isTitle || li.preventClick) && handlerType === 'mouseUp' && onElementClick(e, li)
                        }
                        title={li.tooltip}
                    >
                        {content}
                    </div>
                )}
                {li.separatedBottom && <div className={clsx('rf-list__separator', 'rf-list__separator--bottom')} />}
            </li>
        );
    });

    // -------------------------------------------------------------------------------------------------------------------

    return (
        <ul className={clsx('rf-list', className)} data-testid="rf-list">
            {listElementJSX}
        </ul>
    );
};

export default List;
